import React from "react";
import { Link } from "react-router-dom";
import {
  LifeBuoy,
  ClipboardList,
  MessageCircle,
  Clock,
  Users,
  BarChart2,
  ArrowRight,
  ShieldCheck,
  Zap,
  Ticket,
  CheckCircle2,
  ArrowRightCircle,
  Search
} from "lucide-react";

export default function HelpdeskAndRequests() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section */}
      <div className="bg-white py-16 px-6 border-b border-slate-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[11px] font-bold uppercase tracking-wider mb-6" style={poppinsStyle}>
            <Zap size={14} fill="currentColor" />
            Centralized Employee Support
          </div>
          
          <h1 className="text-3xl md:text-4xl font-medium text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Unified Helpdesk & <br />
            Workplace Request Management
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Empower your teams with a structured ticketing system. Resolve IT, HR, 
            and Facility requests faster with automated routing, real-time tracking, 
            and transparent SLA monitoring.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pb-24">
        
        {/* NEW: Visualized Ticket Lifecycle (Replaces the dashed placeholder) */}
        <div className="mt-12 mb-20 bg-slate-50 rounded-3xl border border-slate-100 p-8 md:p-12 shadow-sm">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-center text-sm font-bold text-slate-400 uppercase tracking-widest mb-10" style={poppinsStyle}>
              The Journey of a Request
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center relative">
              {/* Step 1 */}
              <div className="flex flex-col items-center text-center z-10">
                <div className="w-16 h-16 rounded-2xl bg-white shadow-md flex items-center justify-center text-indigo-600 mb-4 border border-indigo-50">
                  <Ticket size={28} />
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1" style={poppinsStyle}>Submit</h4>
                <p className="text-xs text-gray-500">Employee raises a ticket via portal, mobile, or email.</p>
              </div>

              {/* Step 2 */}
              <div className="flex flex-col items-center text-center z-10">
                <div className="w-16 h-16 rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-200 flex items-center justify-center text-white mb-4">
                  <Search size={28} />
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1" style={poppinsStyle}>Route & Resolve</h4>
                <p className="text-xs text-gray-500">Auto-assigned to the right expert based on SLA priority.</p>
              </div>

              {/* Step 3 */}
              <div className="flex flex-col items-center text-center z-10">
                <div className="w-16 h-16 rounded-2xl bg-emerald-500 shadow-lg shadow-emerald-100 flex items-center justify-center text-white mb-4">
                  <CheckCircle2 size={28} />
                </div>
                <h4 className="font-bold text-gray-900 text-sm mb-1" style={poppinsStyle}>Close & Audit</h4>
                <p className="text-xs text-gray-500">Resolved with feedback and stored for compliance.</p>
              </div>

              {/* Connecting Lines (Desktop only) */}
              <div className="hidden md:block absolute top-8 left-[22%] right-[78%] border-t-2 border-dashed border-slate-200 w-[20%]" />
              <div className="hidden md:block absolute top-8 left-[55%] right-[45%] border-t-2 border-dashed border-slate-200 w-[20%]" />
            </div>
          </div>
        </div>

        {/* Enterprise Feature Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <ServiceFeature 
            icon={<LifeBuoy size={20} />} 
            title="Unified Entry Point"
            desc="One portal for all requests—HR, IT, or Admin—eliminating scattered email threads."
            poppins={poppinsStyle}
          />
          <ServiceFeature 
            icon={<Clock size={20} />} 
            title="SLA Compliance"
            desc="Configure response and resolution timers to ensure high service standards."
            poppins={poppinsStyle}
          />
          <ServiceFeature 
            icon={<MessageCircle size={20} />} 
            title="In-Ticket Chat"
            desc="Keep all communication context-linked. No more hunting through chat apps for history."
            poppins={poppinsStyle}
          />
          <ServiceFeature 
            icon={<Users size={20} />} 
            title="Auto-Assignment"
            desc="Route tickets instantly to specific departments based on request categories."
            poppins={poppinsStyle}
          />
          <ServiceFeature 
            icon={<ShieldCheck size={20} />} 
            title="Audit Transparency"
            desc="Complete log of every status change, comment, and document attached to a ticket."
            poppins={poppinsStyle}
          />
          <ServiceFeature 
            icon={<BarChart2 size={20} />} 
            title="Workload Insights"
            desc="Identify bottlenecks and measure team performance with detailed analytics."
            poppins={poppinsStyle}
          />
        </div>

        {/* Operational Deep-Dive */}
        <div className="grid md:grid-cols-2 gap-16 py-16 border-t border-slate-100">
           <div>
              <h2 className="text-2xl font-medium text-gray-900 mb-6" style={poppinsStyle}>Optimized Resolution Flow</h2>
              <div className="space-y-6">
                 <FlowItem 
                    number="01" 
                    title="Smart Categorization" 
                    desc="Employees select from pre-defined categories which trigger specific custom forms for better data collection." 
                 />
                 <FlowItem 
                    number="02" 
                    title="Priority Ranking" 
                    desc="System assigns 'Critical', 'High', or 'Normal' status based on the impact on business operations." 
                 />
                 <FlowItem 
                    number="03" 
                    title="Knowledge Base Integration" 
                    desc="Suggest relevant FAQ articles automatically as employees type, reducing ticket volume." 
                 />
              </div>
           </div>

           <div className="bg-slate-900 p-10 rounded-3xl text-white relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:scale-110 transition-transform">
                 <LifeBuoy size={120} />
              </div>
              <h3 className="text-xl font-medium mb-4 relative z-10" style={poppinsStyle}>Impact at Scale</h3>
              <p className="text-indigo-200 text-sm leading-relaxed mb-8 relative z-10">
                Large enterprises use our Helpdesk to reduce HR overhead by 30%. By standardizing 
                how requests are made, teams eliminate manual coordination and focus purely 
                on resolving the issue at hand.
              </p>
              <div className="grid grid-cols-2 gap-4 relative z-10">
                 <StatBox label="Avg. Response" value="< 2 Hours" />
                 <StatBox label="Resolution Rate" value="98.4%" />
              </div>
           </div>
        </div>

        {/* Professional Compact CTA */}
        <div className="text-center pt-24 border-t border-slate-100">
          <h3 className="text-xl font-medium text-gray-900 mb-2" style={poppinsStyle}>Ready to streamline your internal support?</h3>
          <p className="text-sm text-gray-500 mb-10">Experience the efficiency of a world-class ticketing system.</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-4 bg-gray-900 hover:bg-indigo-600 text-white px-8 py-3 rounded-full transition-all duration-300 shadow-lg"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Service Portal</p>
                <p className="text-base font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* Internal Components */
function ServiceFeature({ icon, title, desc, poppins }) {
  return (
    <div className="p-6 rounded-2xl border border-slate-100 hover:border-indigo-100 hover:bg-indigo-50/20 transition-all group">
      <div className="w-10 h-10 rounded-lg bg-slate-50 text-slate-500 group-hover:bg-indigo-100 group-hover:text-indigo-600 flex items-center justify-center mb-4 transition-colors">
        {icon}
      </div>
      <h4 className="font-medium text-gray-900 text-base mb-2" style={poppins}>{title}</h4>
      <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
    </div>
  );
}

function FlowItem({ number, title, desc }) {
  return (
    <div className="flex gap-4">
      <span className="text-indigo-200 font-bold text-xl leading-none">{number}</span>
      <div>
        <h4 className="font-medium text-gray-900 text-sm mb-1">{title}</h4>
        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="bg-white/5 border border-white/10 p-4 rounded-2xl">
      <p className="text-[10px] text-indigo-300 uppercase tracking-widest mb-1">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}